import { Captions } from "lucide-react";
import { Callout, EmptyState } from "@/components/ui/feedback";
import { cn } from "@/lib/utils";
import { TranscriptLines, useCallTranscription } from "@/softphone/transcription-widget";

/**
 * The call's live transcript as a pane of the Phone page's work sheet, in the script's place: the
 * script, the lead's texts and this take turns (`softphone/call-panes.tsx`), and the rep brings
 * it up from the phone's rail as they do the script. It was pinned to the foot of the call's card,
 * in a strip held to two fifths of the card's height, so a rep reading a call back had a few lines
 * at a time. As a pane it has the sheet's full height and scrolls inside itself.
 *
 * What it shows is the phone's, not the page's: `transcription-widget.tsx` owns the state above
 * the dock and this, and the same lines are drawn here and in the dock's own panel, pinned to the
 * newest as they arrive. The pane comes up by itself when a transcript starts (`routes/phone.tsx`,
 * `showPane`), and is the rep's to put away and bring back.
 */
export function TranscriptPane({
  className, bodyClassName,
}: {
  className?: string;
  /** For what is under the header only, as the script's pane takes it (`ScriptPane`). */
  bodyClassName?: string;
}) {
  const state = useCallTranscription();
  const hasContent = state.finals.length > 0 || state.partials.length > 0;
  const something = state.on || state.pending || state.error !== null || hasContent;

  return (
    <section aria-label="Live transcript" className={cn("flex min-h-0 flex-col", className)}>
      {/* The same header the other panes have, so side by side they read as one band. */}
      <header className="shrink-0 border-b border-border px-4 py-2.5">
        <div className="flex min-h-7 items-center gap-2">
          <Captions className="size-4 shrink-0 text-accent-11" aria-hidden />
          <h2 className="text-sm font-semibold text-text">Live transcript</h2>
          {state.remoteLabel && (
            <span className="min-w-0 flex-1 truncate font-mono text-2xs text-text-muted">
              {state.remoteLabel}
            </span>
          )}
          {state.on && (
            <span className="relative ml-auto flex size-2 shrink-0" aria-label="Transcribing">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-success-9 opacity-60" />
              <span className="relative inline-flex size-2 rounded-full bg-success-9" />
            </span>
          )}
        </div>
      </header>
      {/* The body: the lines scroll inside it, with the pane's height, the newest at the foot. */}
      <div className={cn("relative flex min-h-0 flex-1 flex-col p-4", bodyClassName)}>
        {state.error && (
          <Callout tone="danger" className="mb-2 shrink-0 text-2xs">
            {state.error}
          </Callout>
        )}
        {something ? (
          // The pane's height, not the popover's strip; and a size for reading, not for a strip.
          <TranscriptLines state={state} className="min-h-0 flex-1 space-y-1.5 text-sm" />
        ) : (
          <EmptyState
            icon={Captions}
            className="min-h-0 flex-1"
            title="No transcript yet"
            description="Press Transcribe on the call, and what is said shows here as it is said."
          />
        )}
      </div>
    </section>
  );
}
